import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AddToSubmissionRequest,
  AuthSelectors,
  BaseAppState,
  OptionalApplicationFeature,
  SignUpRequest,
  SignUpWithSubmissionRequest,
  SurveySelectors,
  getQueryParams,
  signUp,
  signUpWithSubmission,
} from 'ngx-esprio-shared';
import { environment } from 'projects/webk/src/environments/environment';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { SignUpFormValue } from '../sign-up-form/sign-up-form-value.model';

@Component({
  selector: 'app-sign-up-page',
  templateUrl: './sign-up-page.component.html',
})
export class SignUpPageComponent implements OnInit {
  public consultantConnectionDisabled: boolean;
  public signUpPageDisabled: boolean;
  readonly asyncProcessKeys = [signUp.type];

  queryParams$ = this.store.select(getQueryParams);
  private anonymousAnswers$ = this.store.select(SurveySelectors.getAnonymousAnswers);
  private coreData$ = this.store.select(AuthSelectors.getUserCoreData);

  preFilled = {
    patientUserId: '',
    email: '',
  };

  constructor(public store: Store<BaseAppState>) {}

  ngOnInit() {
    this.queryParams$.pipe(take(1)).subscribe((queryParams) => {
      this.preFilled.patientUserId = queryParams.patientUserId;
      this.preFilled.email = queryParams.email;
      this.consultantConnectionDisabled = environment.disabledAppFeatures.includes(
        OptionalApplicationFeature.ConsultantConnection
      );
      this.signUpPageDisabled = environment.disabledAppFeatures.includes(
        OptionalApplicationFeature.SignUpPage
      );
    });
  }

  handleSignUpClick(formValue: SignUpFormValue): void {
    const baseRequest: SignUpRequest = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.emailAddress,
      password: formValue.password,
      patientUserId: this.preFilled.patientUserId,
      referralSourceCode: formValue.referralSourceCode,
    };

    combineLatest([
      this.coreData$.pipe(take(1)),
      this.anonymousAnswers$.pipe(take(1)),
      this.store.select(AuthSelectors.getInvitedByAdvisorId).pipe(take(1)),
    ]).subscribe(([coreData, surveyData, invitedByAdvisorId]) => {
      const signUpRequest: SignUpRequest = invitedByAdvisorId
        ? { ...baseRequest, invitedByAdvisorId }
        : baseRequest;

      const currentWeight = coreData?.initialWeightInKg;
      const previousWeight = coreData?.weightSixMonthsAgoInKg;

      if (coreData && surveyData && Object.keys(surveyData).length > 0) {
        const submissions: AddToSubmissionRequest[] = Object.keys(surveyData).map((groupId) =>
          this.transformSurveyData(groupId, surveyData[groupId])
        );

        const signUpWithSubmissionRequest: SignUpWithSubmissionRequest = {
          signUpRequest,
          coreData,
          submissions,
        };
        this.store.dispatch(signUpWithSubmission({ payload: signUpWithSubmissionRequest }));
      } else {
        this.store.dispatch(signUp({ payload: signUpRequest }));
      }

      if (currentWeight && previousWeight) {
        localStorage.setItem('signupWeights', JSON.stringify({ currentWeight, previousWeight }));
      }
    });
  }

  handleSignUpMonitoringApp(event: Event) {
    event.preventDefault();
    window.location.href = `${environment.monitoringApp}/auth/sign-up`;
  }

  private transformSurveyData(groupId: string, answers: any[]): AddToSubmissionRequest {
    return {
      groupId,
      answers: answers.map((answer) => ({
        surveyElementId: answer.surveyElementId,
        values: answer.values,
      })),
    };
  }
}
